import { CHANNELS, type Channel } from "@stratcms/sdk";
import { api, explain } from "./api";
import { h, icon, toast } from "./dom";
import { editorFor, templateFor } from "./editors";
import { CHANNEL_LABEL } from "./labels";

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);

/** A dialog: pick a channel, write the first draft, save V1. */
export function openNewItem(initial: Channel = "blog") {
  let channel: Channel = initial === "blog" || initial === "linkedin" ? initial : "blog";
  const dlg = h("dialog", { class: "dialog", "aria-labelledby": "new-title" }) as HTMLDialogElement;
  const slug = h("input", { type: "text", required: true, placeholder: "five-tender-checks", pattern: "[a-z0-9]+(-[a-z0-9]+)*", "data-field": "slug" });
  let slugTouched = false;
  slug.addEventListener("input", () => (slugTouched = true));
  const host = h("div", { class: "dialog-body" });
  let editor = editorFor(channel, templateFor(channel), false);
  const draw = () => {
    editor = editorFor(channel, templateFor(channel), false);
    host.replaceChildren(editor.el);
    editor.onChange(() => {
      if (slugTouched) return;
      const p = editor.read() as Record<string, string>;
      const base = p.title || p.documentTitle || (p.text || p.caption || "").split("\n")[0] || "";
      slug.value = slugify(channel === "linkedin" ? `${new Date(Date.now() + 2 * 3600_000).toISOString().slice(0, 10)}-${base}` : base);
    });
  };
  const chips = h(
    "div",
    { class: "chips", role: "radiogroup", "aria-label": "Channel" },
    // Instagram and YouTube need uploaded media, and uploads are off (no R2): offer only what can work.
    ...CHANNELS.filter((c) => c === "blog" || c === "linkedin").map((c) =>
      h(
        "button",
        {
          type: "button",
          role: "radio",
          class: `chip${c === channel ? " on" : ""}`,
          "aria-checked": c === channel ? "true" : "false",
          onclick: (e: Event) => {
            channel = c;
            for (const b of chips.querySelectorAll("button")) {
              b.classList.toggle("on", b === e.currentTarget);
              b.setAttribute("aria-checked", b === e.currentTarget ? "true" : "false");
            }
            draw();
          },
        },
        CHANNEL_LABEL[c],
      ),
    ),
  );
  const save = h("button", { class: "btn btn-primary", type: "submit", "data-testid": "create-draft" }, icon("check"), h("span", {}, "Save draft (V1)"));
  const form = h(
    "form",
    {
      method: "dialog",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        save.setAttribute("disabled", "");
        try {
          const r = await api.create({ channel, slug: slug.value.trim(), payload: editor.read() });
          dlg.close();
          dlg.remove();
          toast(r.sanitized ? "Draft saved. Unsafe HTML was removed from the body." : "Draft saved as V1.", "ok");
          location.hash = `#/item/${r.item.id}`;
        } catch (err) {
          toast(explain(err), "error");
        } finally {
          save.removeAttribute("disabled");
        }
      },
    },
    h("header", { class: "dialog-head" }, h("h2", { id: "new-title" }, "New content"), h("button", { type: "button", class: "icon-btn", "aria-label": "Close", onclick: () => (dlg.close(), dlg.remove()) }, icon("x"))),
    chips,
    h("p", { class: "fine", "data-testid": "uploads-off" }, "Instagram and YouTube need uploaded media. Uploads are off because Strat CMS has no file storage (R2 is not enabled); blog images come from the website's /img folder."),
    h("label", { class: "field" }, h("span", { class: "field-label" }, "Slug (the web address, never changes)"), slug),
    host,
    h("footer", { class: "dialog-foot" }, h("button", { type: "button", class: "btn btn-ghost", onclick: () => (dlg.close(), dlg.remove()) }, "Cancel"), save),
  );
  dlg.appendChild(form);
  document.body.appendChild(dlg);
  draw();
  dlg.showModal();
}
